/* Copyright 2021, Milkdown by Mirone. */
import { editorViewCtx } from '@milkdown/core';
import React, { forwardRef, useImperativeHandle } from 'react';
import { portalContext, Portals } from './Portals';
import { createReactView } from './ReactNodeView';
const useGetEditor = (getEditor) => {
    const renderReact = React.useContext(portalContext);
    const divRef = React.useRef(null);
    const editorRef = React.useRef();
    React.useEffect(() => {
        const div = divRef.current;
        if (!div)
            return;
        const editor = getEditor(div, renderReact);
        if (!editor)
            return;
        editor
            .create()
            .then((editor) => {
            editorRef.current = editor;
            return;
        })
            .catch(console.error);
        return () => {
            var _a, _b;
            const view = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.action((ctx) => ctx.get(editorViewCtx));
            if (!view)
                return;
            (_b = view.dom.parentElement) === null || _b === void 0 ? void 0 : _b.remove();
            view.destroy();
        };
    }, [getEditor, renderReact]);
    return { divRef, editorRef };
};
export const EditorComponent = forwardRef(({ editor }, ref) => {
    const refs = useGetEditor(editor);
    useImperativeHandle(ref, () => ({
        get: () => {
            return refs.editorRef.current;
        },
        dom: () => {
            return refs.divRef.current;
        },
    }));
    return React.createElement("div", { ref: refs.divRef });
});
export const ReactEditor = forwardRef(({ editor }, ref) => {
    const [portals, setPortals] = React.useState([]);
    const addPortal = React.useCallback((portal) => {
        setPortals((ps) => [...ps, portal]);
    }, []);
    const removePortalByKey = React.useCallback((key) => {
        setPortals((x) => {
            const index = x.findIndex((p) => p.key === key);
            return [...x.slice(0, index), ...x.slice(index + 1)];
        });
    }, []);
    const renderReact = React.useCallback((Component) => createReactView(addPortal, removePortalByKey)(Component), [addPortal, removePortalByKey]);
    return (React.createElement(portalContext.Provider, { value: renderReact },
        React.createElement(Portals, { portals: portals }),
        React.createElement(EditorComponent, { ref: ref, editor: editor })));
});
export const useEditor = (getEditor, deps = []) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return React.useCallback((...args) => getEditor(...args), deps);
};
//# sourceMappingURL=Editor.js.map